import { computed, onScopeDispose, ref } from "vue"

/**
 * "A view has taken over the screen — stand down."
 *
 * `LewBaseLayout` reads this to drop the app bar and the navigation drawer while a view is
 * showing something full-screen, and `plugins/sentry.js` to hide the bug-report button, which
 * would otherwise lie on the view's bottom-right corner.
 *
 * Painting over them does work, but only since `.page-enter` stopped leaving a filling animation
 * (and so a permanent stacking context) on `v-main` - see the note in `globals.scss`. This is the
 * other half: a maximised diagram with a dead app bar and an unreachable navigation drawer lying
 * on top of it is not full screen, however correct the z-index. Unmounting them is what the
 * feature actually means, and it cannot be re-broken by a stacking context somewhere above.
 *
 * Module scope on purpose: the layout and the view that takes over are on opposite sides of the
 * component tree, and there is only ever one screen to take over.
 */
const immersiveCount = ref(0)

/** For readers outside a component scope, where `useImmersiveMode` would register a disposer on nothing. */
export const isImmersive = computed(() => immersiveCount.value > 0)

/**
 * A counter rather than a boolean so two views mid-transition (the old one unmounting after the
 * new one mounted) cannot leave the chrome hidden on a page that never asked for it.
 */
export function useImmersiveMode() {
  /** This scope's own hold, so releasing is idempotent and never decrements someone else's. */
  let held = false

  const setImmersive = (active: boolean) => {
    if (active === held) return
    held = active
    immersiveCount.value += active ? 1 : -1
  }

  // A view that is torn down while immersive - route change, popout, an error - must not take the
  // app bar with it.
  onScopeDispose(() => setImmersive(false))

  return {
    isImmersive,
    setImmersive,
  }
}
